import {ref} from "valtio";

import {EventEmitter} from "@/shared/event-emitter";
import {isClient, isServer} from "@/shared/utils/runtime";

export interface NavigateOptions {
    /**
     * Scroll to top after navigation
     * @default false
     */
    scroll?: boolean;
    /**
     * State object to store in history
     * Must be serializable (no functions, classes, circular refs)
     * Size limit: ~640KB (Firefox) to ~10MB (Chrome)
     */
    state?: Record<string, unknown>;
    /**
     * Whether to show global progress bar/loader
     * @default true
     */
    showProgressBar?: boolean;
}

/**
 * Common interface for all routers
 */
export interface IRouter {
    navigate(path: string, options?: NavigateOptions): void;
    replace(path: string, options?: NavigateOptions): void;
    back(): void;
    forward(): void;
    refresh(): void;
    dispose(): void;
}

interface RouterBridgeEvents {
    back: () => void;
    forward: () => void;
    refresh: () => void;
}

/**
 * Client-side Router - shallow routing using Native History API
 * Does not trigger server component re-render
 * @see https://nextjs.org/docs/app/getting-started/linking-and-navigating#using-the-native-history-api
 */
class RouterBridgeClass implements IRouter {
    readonly events: EventEmitter<RouterBridgeEvents>;

    /**
     * Tracks the query string we're about to navigate to internally.
     * Used by useRouterBridge to skip onUrlChange for our own navigations.
     */
    pendingQueryString: string | null = null;

    constructor() {
        this.events = ref(new EventEmitter<RouterBridgeEvents>()) as EventEmitter<RouterBridgeEvents>;

        // Disable automatic scroll restoration by the browser
        if (isClient() && "scrollRestoration" in window.history) {
            window.history.scrollRestoration = "manual";
        }
    }

    /**
     * Navigate to a new URL using Native History API
     * Adds a new entry to the browser's history stack
     *
     * Next.js automatically syncs with usePathname and useSearchParams
     * without reloading the page or triggering server component re-render
     *
     * @see https://nextjs.org/docs/app/getting-started/linking-and-navigating#using-the-native-history-api
     */
    navigate(path: string, options?: NavigateOptions): void {
        if (isServer()) return;

        const {scroll = false, state} = options || {};
        // ✅ Shallow routing - updates URL without server component rerender
        // Next.js automatically syncs usePathname and useSearchParams
        // Note: state should be null or undefined to avoid conflicts with Next.js internal state
        window.history.pushState(state ?? null, "", path);
        if (scroll) {
            window.scrollTo({top: 0, behavior: "smooth"});
        }
    }

    /**
     * Replace current URL without adding to history
     * User cannot navigate back to the previous state
     *
     * Next.js automatically syncs with usePathname and useSearchParams
     *
     * @see https://nextjs.org/docs/app/getting-started/linking-and-navigating#windowhistoryreplacestate
     */
    replace(path: string, options?: NavigateOptions): void {
        if (isServer()) return;

        const {scroll = false, state = null} = options || {};

        // ✅ Shallow replace - replaces URL without adding to history
        // Next.js automatically syncs usePathname and useSearchParams
        // Note: state should be null to avoid conflicts with Next.js internal state
        window.history.replaceState(state, "", path);

        if (scroll) {
            window.scrollTo({top: 0, behavior: "smooth"});
        }
    }

    /**
     * Update only search params while preserving current pathname
     * Adds a new entry to browser history
     */
    updateSearchParams(queryString: string, options?: NavigateOptions): void {
        if (isServer()) return;

        // Mark this as our own navigation so useRouterBridge can skip onUrlChange
        this.pendingQueryString = queryString;

        const currentPath = window.location.pathname;
        const newPath = queryString ? `${currentPath}?${queryString}` : currentPath;
        this.navigate(newPath, options);
    }

    /**
     * Replace search params while preserving current pathname
     * Does NOT add to browser history
     */
    replaceSearchParams(queryString: string, options?: NavigateOptions): void {
        if (isServer()) return;

        // Mark this as our own navigation so useRouterBridge can skip onUrlChange
        this.pendingQueryString = queryString;

        const currentPath = window.location.pathname;
        const newPath = queryString ? `${currentPath}?${queryString}` : currentPath;
        this.replace(newPath, {state: window.history.state, ...options});
    }

    /**
     * Get current history state
     * Returns null if no state or on server
     */
    getState<T = Record<string, unknown>>(): T | null {
        if (isServer()) return null;
        return window.history.state as T | null;
    }

    /**
     * Get current search params as string (without leading '?')
     */
    getSearchParams(): string {
        if (isServer()) return "";
        return window.location.search.slice(1);
    }

    /**
     * Get current pathname
     */
    getPathname(): string {
        if (isServer()) return "";
        return window.location.pathname;
    }

    /**
     * Get current URL (pathname + search params)
     */
    getUrl(): string {
        if (isServer()) return "";
        const search = window.location.search;
        return `${window.location.pathname}${search}`;
    }

    back(): void {
        if (isServer()) return;
        window.history.back();
    }

    forward(): void {
        if (isServer()) return;
        window.history.forward();
    }

    refresh(): void {
        this.events.emit("refresh");
    }

    dispose(): void {
        this.events.removeAllListeners();
    }
}

export type RouterBridgeInstance = RouterBridgeClass;

export function RouterBridge(): RouterBridgeInstance {
    return new RouterBridgeClass();
}
